<template>
  <ListPageShell>
    <template #header>
      <div class="list-page-panel is-header-minimal px-4 py-3 md:px-6">
        <div class="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <h1 class="list-page-title">电子签模板</h1>
            <p class="mt-2 text-sm leading-6 text-slate-600">维护平台电子签模板、签署方角色和控件定位，支撑后台发起腾讯电子签流程。</p>
          </div>
          <div class="flex flex-wrap gap-2">
            <button type="button" class="btn-secondary inline-flex items-center gap-2" @click="loadTencentTemplates">
              <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v6h6M20 20v-6h-6M20 9A8 8 0 006.7 4.7L4 7m0 8a8 8 0 0013.3 4.3L20 17" />
              </svg>
              拉取腾讯模板
            </button>
            <button type="button" class="btn-primary inline-flex items-center gap-2" @click="openCreate">
              <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
              </svg>
              创建模板
            </button>
          </div>
        </div>
      </div>
    </template>

    <div class="space-y-4">
      <div class="grid gap-3 md:grid-cols-4">
        <div v-for="item in summaryCards" :key="item.label" class="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <p class="text-xs font-medium text-slate-500">{{ item.label }}</p>
          <p class="mt-2 text-2xl font-semibold text-slate-900">{{ item.value }}</p>
        </div>
      </div>

      <div class="list-page-table-panel">
        <PageFilterBar
          shell-class="filter-bar-shell is-embedded"
          body-class="filter-bar-body is-panel"
          :show-refresh="true"
          :refresh-loading="loading"
          @refresh="fetchList"
        >
          <div class="filter-bar-field is-wide relative">
            <input v-model="filters.keyword" class="form-search-input form-control-sm w-full" placeholder="搜索模板名称或编码" @keyup.enter="handleSearch" />
            <svg class="absolute left-3 top-2.5 h-4 w-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <el-select v-model="filters.templateType" placeholder="请选择模板类型" class="form-select form-control-sm filter-bar-field">
            <el-option label="全部类型" value="" />
            <el-option label="文件模板" value="FILE" />
            <el-option label="腾讯模板" value="TENCENT_TEMPLATE" />
          </el-select>
          <el-select v-model="filters.status" placeholder="请选择状态" class="form-select form-control-sm filter-bar-field">
            <el-option label="全部状态" value="" />
            <el-option label="草稿" value="DRAFT" />
            <el-option label="启用" value="ENABLED" />
            <el-option label="停用" value="DISABLED" />
          </el-select>
          <template #searchActions>
            <button type="button" class="btn-primary" @click="handleSearch">搜索</button>
            <button type="button" class="btn-secondary" @click="handleReset">重置</button>
          </template>
        </PageFilterBar>

        <div class="data-table-shell border-none shadow-none">
          <div class="data-table-scroll">
            <table class="data-table text-sm">
              <thead class="data-table-head">
                <tr>
                  <th class="data-table-th text-left">模板</th>
                  <th class="data-table-th text-left">类型</th>
                  <th class="data-table-th text-left">渠道</th>
                  <th class="data-table-th text-left">腾讯模板 ID</th>
                  <th class="data-table-th text-left">状态</th>
                  <th class="data-table-th text-left">更新时间</th>
                  <th class="data-table-th is-center">操作</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="loading" class="data-table-row">
                  <td colspan="7" class="data-table-empty">正在加载模板...</td>
                </tr>
                <tr v-else-if="!list.length" class="data-table-row">
                  <td colspan="7" class="data-table-empty">暂无电子签模板</td>
                </tr>
                <tr v-for="item in list" v-else :key="item.id" class="data-table-row">
                  <td class="data-table-td min-w-[240px]">
                    <div class="data-table-cell-title">{{ item.templateName || item.name || '-' }}</div>
                    <div class="data-table-cell-meta">{{ item.templateCode || item.code || item.id }}</div>
                  </td>
                  <td class="data-table-td whitespace-nowrap">{{ templateTypeText(item.templateType) }}</td>
                  <td class="data-table-td whitespace-nowrap">{{ item.channel || 'TENCENT' }}</td>
                  <td class="data-table-td whitespace-nowrap font-mono text-xs text-slate-600">{{ blank(item.tencentTemplateId) }}</td>
                  <td class="data-table-td whitespace-nowrap">
                    <span :class="['status-badge', templateStatusClass(item.status)]">{{ templateStatusText(item.status) }}</span>
                  </td>
                  <td class="data-table-td whitespace-nowrap text-slate-600">{{ formatDateTime(item.updateTime || item.updatedAt || item.createTime) || '-' }}</td>
                  <td class="data-table-td is-center whitespace-nowrap">
                    <div class="flex items-center justify-center gap-1">
                      <button class="table-action-button is-primary" title="详情" aria-label="详情" @click="openDetail(item)">详情</button>
                      <button class="table-action-button is-indigo" title="签署方" aria-label="签署方" @click="openParties(item)">签署方</button>
                      <button class="table-action-button is-teal" title="控件" aria-label="控件" @click="openFields(item)">控件</button>
                      <button v-if="item.status !== 'ENABLED'" class="table-action-button is-success" title="启用" aria-label="启用" @click="toggleTemplate(item, true)">启用</button>
                      <button v-else class="table-action-button is-danger" title="停用" aria-label="停用" @click="toggleTemplate(item, false)">停用</button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <AppPagination
          class="pagination-bridge"
          :pagination="pagination"
          :page-sizes="[10, 20, 50]"
          @page-change="handlePageChange"
          @size-change="handlePageSizeChange"
        />
      </div>
    </div>

    <el-drawer v-model="detailVisible" title="模板详情" size="640px">
      <div v-if="detail" class="space-y-6">
        <section class="rounded-2xl border border-slate-200 p-4">
          <h3 class="text-sm font-semibold text-slate-900">基础信息</h3>
          <dl class="mt-4 grid grid-cols-2 gap-3 text-sm">
            <div><dt class="text-slate-500">模板名称</dt><dd class="mt-1 text-slate-900">{{ detail.templateName || detail.name || '-' }}</dd></div>
            <div><dt class="text-slate-500">模板编码</dt><dd class="mt-1 text-slate-900">{{ detail.templateCode || detail.code || '-' }}</dd></div>
            <div><dt class="text-slate-500">模板类型</dt><dd class="mt-1 text-slate-900">{{ templateTypeText(detail.templateType) }}</dd></div>
            <div><dt class="text-slate-500">腾讯模板 ID</dt><dd class="mt-1 font-mono text-xs text-slate-900">{{ blank(detail.tencentTemplateId) }}</dd></div>
          </dl>
        </section>
        <section class="rounded-2xl border border-slate-200 p-4">
          <h3 class="text-sm font-semibold text-slate-900">签署方</h3>
          <div class="mt-3 space-y-2">
            <div v-for="party in detailParties" :key="party.id || party.roleCode" class="rounded-xl bg-slate-50 p-3 text-sm">
              <div class="font-medium text-slate-900">{{ party.roleName || party.roleCode || '-' }}</div>
              <div class="mt-1 text-slate-500">{{ party.subjectType || '-' }} / {{ party.approverType || '-' }} / 顺序 {{ party.signOrder ?? '-' }}</div>
            </div>
            <p v-if="!detailParties.length" class="text-sm text-slate-500">暂无签署方配置</p>
          </div>
        </section>
        <section class="rounded-2xl border border-slate-200 p-4">
          <h3 class="text-sm font-semibold text-slate-900">控件字段</h3>
          <div class="mt-3 space-y-2">
            <div v-for="field in detailFields" :key="field.id || field.fieldCode" class="rounded-xl bg-slate-50 p-3 text-sm">
              <div class="font-medium text-slate-900">{{ field.fieldName || field.fieldCode || '-' }}</div>
              <div class="mt-1 text-slate-500">{{ field.fieldType || '-' }} / {{ field.generateMode || 'POSITION' }} / 第 {{ field.pageNo || '-' }} 页</div>
            </div>
            <p v-if="!detailFields.length" class="text-sm text-slate-500">暂无控件配置</p>
          </div>
        </section>
      </div>
    </el-drawer>

    <el-dialog v-model="createVisible" title="创建电子签模板" width="640px">
      <el-form label-width="120px">
        <el-form-item label="模板编码" required><input v-model="templateForm.templateCode" class="form-input w-full" /></el-form-item>
        <el-form-item label="模板名称" required><input v-model="templateForm.templateName" class="form-input w-full" /></el-form-item>
        <el-form-item label="模板类型">
          <el-select v-model="templateForm.templateType" class="form-select w-full">
            <el-option label="文件模板" value="FILE" />
            <el-option label="腾讯模板" value="TENCENT_TEMPLATE" />
          </el-select>
        </el-form-item>
        <el-form-item label="渠道"><input v-model="templateForm.channel" class="form-input w-full" /></el-form-item>
        <el-form-item label="腾讯模板 ID"><input v-model="templateForm.tencentTemplateId" class="form-input w-full" /></el-form-item>
        <el-form-item label="备注"><textarea v-model="templateForm.remark" class="form-textarea w-full" rows="3" /></el-form-item>
      </el-form>
      <template #footer>
        <button class="btn-secondary" type="button" @click="createVisible = false">取消</button>
        <button class="btn-primary ml-2" type="button" :disabled="submitting" @click="submitCreate">保存</button>
      </template>
    </el-dialog>

    <el-dialog v-model="partiesVisible" title="配置签署方" width="900px">
      <div class="space-y-3">
        <div v-for="(party, index) in partyRows" :key="party.key" class="grid gap-3 rounded-2xl border border-slate-200 p-3 md:grid-cols-6">
          <input v-model="party.roleCode" class="form-input" placeholder="角色编码" />
          <input v-model="party.roleName" class="form-input" placeholder="角色名称" />
          <el-select v-model="party.subjectType" class="form-select">
            <el-option label="个人" value="PERSON" />
            <el-option label="企业" value="ORGANIZATION" />
          </el-select>
          <el-select v-model="party.approverType" class="form-select">
            <el-option label="个人签署" value="PERSON" />
            <el-option label="企业签署" value="ORGANIZATION" />
            <el-option label="企业静默签署" value="ORGANIZATION_AUTO_SIGN" />
            <el-option label="个人自动签署" value="PERSON_AUTO_SIGN_RESERVED" />
          </el-select>
          <input v-model.number="party.signOrder" type="number" class="form-input" placeholder="顺序" />
          <button type="button" class="btn-danger-secondary" @click="removeParty(index)">删除</button>
        </div>
        <button type="button" class="btn-secondary" @click="addParty">添加签署方</button>
      </div>
      <template #footer>
        <button class="btn-secondary" type="button" @click="partiesVisible = false">取消</button>
        <button class="btn-primary ml-2" type="button" :disabled="submitting" @click="submitParties">保存</button>
      </template>
    </el-dialog>

    <el-dialog v-model="fieldsVisible" title="配置控件字段" width="1080px">
      <div class="space-y-3">
        <div v-for="(field, index) in fieldRows" :key="field.key" class="grid gap-3 rounded-2xl border border-slate-200 p-3 md:grid-cols-8">
          <input v-model="field.fieldCode" class="form-input" placeholder="字段编码" />
          <input v-model="field.fieldName" class="form-input" placeholder="字段名称" />
          <el-select v-model="field.fieldType" class="form-select">
            <el-option label="文本" value="TEXT" />
            <el-option label="个人签名" value="SIGNATURE" />
            <el-option label="企业公章" value="SEAL" />
            <el-option label="骑缝章" value="PAGING_SEAL" />
            <el-option label="法人章" value="LEGAL_PERSON_SEAL" />
            <el-option label="勾选框" value="CHECKBOX" />
            <el-option label="日期" value="DATE" />
            <el-option label="金额" value="AMOUNT" />
            <el-option label="图片" value="IMAGE" />
          </el-select>
          <el-select v-model="field.generateMode" class="form-select">
            <el-option label="坐标" value="POSITION" />
            <el-option label="关键字" value="KEYWORD" />
            <el-option label="表单域" value="FIELD" />
          </el-select>
          <input v-model="field.componentId" class="form-input" placeholder="关键字/域名" />
          <input v-model.number="field.pageNo" type="number" class="form-input" placeholder="页码" />
          <input v-model="field.defaultValue" class="form-input" placeholder="默认值/印章ID" />
          <button type="button" class="btn-danger-secondary" @click="removeField(index)">删除</button>
        </div>
        <button type="button" class="btn-secondary" @click="addField">添加控件</button>
      </div>
      <template #footer>
        <button class="btn-secondary" type="button" @click="fieldsVisible = false">取消</button>
        <button class="btn-primary ml-2" type="button" :disabled="submitting" @click="submitFields">保存</button>
      </template>
    </el-dialog>

    <el-dialog v-model="tencentVisible" title="腾讯模板列表" width="860px">
      <div class="data-table-shell">
        <div class="data-table-scroll">
          <table class="data-table text-sm">
            <thead class="data-table-head">
              <tr>
                <th class="data-table-th text-left">模板 ID</th>
                <th class="data-table-th text-left">模板名称</th>
                <th class="data-table-th text-left">状态</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in tencentTemplates" :key="item.templateId || item.id" class="data-table-row">
                <td class="data-table-td font-mono text-xs">{{ item.templateId || item.id }}</td>
                <td class="data-table-td">{{ item.templateName || item.name || '-' }}</td>
                <td class="data-table-td">{{ item.status || '-' }}</td>
              </tr>
              <tr v-if="!tencentTemplates.length"><td colspan="3" class="data-table-empty">暂无腾讯模板</td></tr>
            </tbody>
          </table>
        </div>
      </div>
    </el-dialog>
  </ListPageShell>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import AppPagination from '@/components/AppPagination.vue'
import ListPageShell from '@/components/ListPageShell.vue'
import PageFilterBar from '@/components/PageFilterBar.vue'
import {
  createEsignTemplate,
  disableEsignTemplate,
  enableEsignTemplate,
  getEsignTemplateDetail,
  getEsignTemplatePage,
  getTencentEsignTemplates,
  saveEsignTemplateFields,
  saveEsignTemplateParties
} from '@/api/esign'
import {
  blank,
  formatDateTime,
  normalizePage,
  readArray,
  templateStatusClass,
  templateStatusText
} from './esign-utils'

const loading = ref(false)
const submitting = ref(false)
const list = ref([])
const detail = ref(null)
const currentTemplate = ref(null)
const tencentTemplates = ref([])
const detailVisible = ref(false)
const createVisible = ref(false)
const partiesVisible = ref(false)
const fieldsVisible = ref(false)
const tencentVisible = ref(false)

const filters = reactive({ keyword: '', templateType: '', status: '' })
const pagination = ref({ current: 1, size: 10, total: 0, pages: 1 })
const templateForm = reactive({ templateCode: '', templateName: '', templateType: 'FILE', channel: 'TENCENT', tencentTemplateId: '', remark: '' })
const partyRows = ref([])
const fieldRows = ref([])

const detailParties = computed(() => readArray(detail.value?.parties || detail.value?.partyList))
const detailFields = computed(() => readArray(detail.value?.fields || detail.value?.fieldList))
const summaryCards = computed(() => [
  { label: '当前页模板', value: list.value.length },
  { label: '启用', value: list.value.filter(item => item.status === 'ENABLED').length },
  { label: '草稿', value: list.value.filter(item => item.status === 'DRAFT').length },
  { label: '停用', value: list.value.filter(item => item.status === 'DISABLED').length }
])

const templateTypeText = type => ({ FILE: '文件模板', TENCENT_TEMPLATE: '腾讯模板' }[type] || blank(type))
const resetTemplateForm = () => Object.assign(templateForm, { templateCode: '', templateName: '', templateType: 'FILE', channel: 'TENCENT', tencentTemplateId: '', remark: '' })

const fetchList = async () => {
  loading.value = true
  try {
    const res = await getEsignTemplatePage({ current: pagination.value.current, size: pagination.value.size, ...filters })
    const page = normalizePage(res, pagination.value)
    list.value = page.records
    pagination.value = page.pagination
  } catch (error) {
    ElMessage.error(error.message || '加载电子签模板失败')
  } finally {
    loading.value = false
  }
}

const handleSearch = () => {
  pagination.value.current = 1
  fetchList()
}

const handleReset = () => {
  Object.assign(filters, { keyword: '', templateType: '', status: '' })
  handleSearch()
}

const handlePageChange = (page) => {
  pagination.value.current = page
  fetchList()
}

const handlePageSizeChange = (size) => {
  pagination.value.size = size
  pagination.value.current = 1
  fetchList()
}

const openCreate = () => {
  resetTemplateForm()
  createVisible.value = true
}

const submitCreate = async () => {
  if (!templateForm.templateCode || !templateForm.templateName) {
    ElMessage.warning('请填写模板编码和模板名称')
    return
  }
  submitting.value = true
  try {
    await createEsignTemplate({ ...templateForm })
    ElMessage.success('模板已创建')
    createVisible.value = false
    fetchList()
  } catch (error) {
    ElMessage.error(error.message || '创建模板失败')
  } finally {
    submitting.value = false
  }
}

const openDetail = async (item) => {
  detailVisible.value = true
  detail.value = null
  try {
    detail.value = await getEsignTemplateDetail(item.id)
  } catch (error) {
    ElMessage.error(error.message || '加载模板详情失败')
  }
}

const toggleTemplate = async (item, enabled) => {
  await ElMessageBox.confirm(`确认${enabled ? '启用' : '停用'}模板「${item.templateName || item.templateCode}」吗？`, enabled ? '启用模板' : '停用模板', { type: 'warning' })
  try {
    if (enabled) await enableEsignTemplate(item.id)
    else await disableEsignTemplate(item.id)
    ElMessage.success(enabled ? '模板已启用' : '模板已停用')
    fetchList()
  } catch (error) {
    ElMessage.error(error.message || '操作失败')
  }
}

const openParties = (item) => {
  currentTemplate.value = item
  partyRows.value = readArray(item.parties || item.partyList).map((party, index) => ({ key: `${Date.now()}-${index}`, ...party }))
  if (!partyRows.value.length) addParty()
  partiesVisible.value = true
}

const addParty = () => partyRows.value.push({ key: `${Date.now()}-${Math.random()}`, roleCode: '', roleName: '', subjectType: 'PERSON', approverType: 'PERSON', signOrder: partyRows.value.length + 1 })
const removeParty = index => partyRows.value.splice(index, 1)

const submitParties = async () => {
  submitting.value = true
  try {
    const parties = partyRows.value.map((row) => {
      const party = { ...row }
      delete party.key
      return party
    }).filter(party => party.roleCode || party.roleName)
    await saveEsignTemplateParties(currentTemplate.value.id, { parties })
    ElMessage.success('签署方配置已保存')
    partiesVisible.value = false
    fetchList()
  } catch (error) {
    ElMessage.error(error.message || '保存签署方失败')
  } finally {
    submitting.value = false
  }
}

const openFields = (item) => {
  currentTemplate.value = item
  fieldRows.value = readArray(item.fields || item.fieldList).map((field, index) => ({ key: `${Date.now()}-${index}`, ...field }))
  if (!fieldRows.value.length) addField()
  fieldsVisible.value = true
}

const addField = () => fieldRows.value.push({ key: `${Date.now()}-${Math.random()}`, fieldCode: '', fieldName: '', fieldType: 'TEXT', generateMode: 'POSITION', componentId: '', pageNo: 1, defaultValue: '' })
const removeField = index => fieldRows.value.splice(index, 1)

const submitFields = async () => {
  submitting.value = true
  try {
    const fields = fieldRows.value.map((row) => {
      const field = { ...row }
      delete field.key
      return field
    }).filter(field => field.fieldCode || field.fieldName)
    await saveEsignTemplateFields(currentTemplate.value.id, { fields })
    ElMessage.success('控件配置已保存')
    fieldsVisible.value = false
    fetchList()
  } catch (error) {
    ElMessage.error(error.message || '保存控件失败')
  } finally {
    submitting.value = false
  }
}

const loadTencentTemplates = async () => {
  try {
    const res = await getTencentEsignTemplates({})
    tencentTemplates.value = readArray(res)
    tencentVisible.value = true
  } catch (error) {
    ElMessage.error(error.message || '查询腾讯模板失败')
  }
}

onMounted(fetchList)
</script>
